import { useState } from "react";
import { Trash2, BookOpen, CheckCircle, Search, Columns3, GripVertical, ArrowUp, ArrowDown } from "lucide-react";
import { Input } from "@/components/ui/input";

const CREDIT_STRATS = new Set([
  "bull_put_spread", "bear_call_spread", "iron_condor", "iron_butterfly",
  "short_put", "cash_secured_put", "short_call", "covered_call",
  "strangle", "straddle",
]);
const DEBIT_STRATS = new Set([
  "long_put", "long_call", "bull_call_spread", "bear_put_spread",
  "protective_put", "married_put", "collar",
  "calendar", "calendar_spread", "diagonal", "diagonal_spread",
]);

export const GRID_COLUMNS = [
  { key: "instrument", label: "Instrument", align: "left", sortKey: "ticker" },
  { key: "strike", label: "Strike", align: "right", sortKey: "strike" },
  { key: "entry", label: "Entry", align: "right", sortKey: "entry" },
  { key: "exit", label: "Exit", align: "right", sortKey: "exit" },
  { key: "pnl", label: "P&L", align: "right", sortKey: "pnl" },
  { key: "currpct", label: "Curr %P/L", align: "right", sortKey: "currpct" },
  { key: "status", label: "Status", align: "left", sortKey: "status" },
  { key: "date", label: "Date", align: "left", sortKey: "date" },
];

export const DEFAULT_GRID_PREFS = {
  order: GRID_COLUMNS.map(c => c.key),
  hidden: [],
  sort: { key: "date", dir: "desc" },
};

const isOptions = (t) => t.strategy && t.strategy !== "long_stock" && t.strategy !== "short_stock";
const strikeDisplay = (t) => {
  if (!isOptions(t)) return "—";
  if (t.short_strike && t.long_strike) return `$${t.short_strike}/$${t.long_strike}`;
  if (t.short_strike) return `$${t.short_strike}`;
  if (t.long_strike) return `$${t.long_strike}`;
  return "—";
};
const currPnlPct = (t) => {
  if (t.status !== "open" || !t.current_price || !t.entry_price) return null;
  return ((t.current_price - t.entry_price) / Math.abs(t.entry_price) * 100);
};

const sortVal = (t, key) => {
  switch (key) {
    case "ticker": return t.ticker || "";
    case "strike": return t.short_strike ?? t.long_strike ?? -Infinity;
    case "entry": return t.entry_price ?? -Infinity;
    case "exit": return t.exit_price ?? -Infinity;
    case "pnl": return t.pnl ?? -Infinity;
    case "currpct": { const v = currPnlPct(t); return v === null ? -Infinity : v; }
    case "status": return t.status || "";
    case "date": return t.entry_date || t.exit_date || "";
    default: return "";
  }
};

export const TradeLogTable = ({ trades, tab, search, onSearchChange, prefs, onPrefsChange, onEdit, onDelete, onCloseTrade }) => {
  const [dragKey, setDragKey] = useState(null);
  const [showCols, setShowCols] = useState(false);

  const visibleCols = prefs.order
    .map(k => GRID_COLUMNS.find(c => c.key === k))
    .filter(c => c && !prefs.hidden.includes(c.key));

  const setSort = (sortKey) => {
    const dir = prefs.sort.key === sortKey && prefs.sort.dir === "desc" ? "asc" : "desc";
    onPrefsChange({ ...prefs, sort: { key: sortKey, dir } });
  };

  const reorder = (fromKey, toKey) => {
    if (!fromKey || fromKey === toKey) return;
    const order = [...prefs.order];
    const fi = order.indexOf(fromKey), ti = order.indexOf(toKey);
    if (fi < 0 || ti < 0) return;
    order.splice(fi, 1);
    order.splice(ti, 0, fromKey);
    onPrefsChange({ ...prefs, order });
  };

  const toggleCol = (key) => {
    const hidden = prefs.hidden.includes(key)
      ? prefs.hidden.filter(k => k !== key)
      : [...prefs.hidden, key];
    onPrefsChange({ ...prefs, hidden });
  };

  const q = (search || "").trim().toUpperCase();
  let rows = q ? trades.filter(t => (t.ticker || "").toUpperCase().includes(q)) : [...trades];
  const { key: sk, dir } = prefs.sort;
  rows.sort((a, b) => {
    const va = sortVal(a, sk), vb = sortVal(b, sk);
    const cmp = typeof va === "string" ? va.localeCompare(vb) : va - vb;
    return dir === "asc" ? cmp : -cmp;
  });

  const renderCell = (t, key) => {
    switch (key) {
      case "instrument":
        return (
          <td key={key} className="font-mono-data text-xs font-medium">
            <span className="text-blue-400">{t.ticker}</span>
            {isOptions(t) && (
              <>
                <span className="text-[9px] ml-1" style={{ color: "var(--hoip-text-tertiary)" }}>· {t.strategy?.replace(/_/g, " ")}{t.short_strike ? ` · $${t.short_strike}` : t.long_strike ? ` · $${t.long_strike}` : ""}{t.expiry ? ` · ${t.expiry.slice(5)}` : ""}</span>
                {(() => {
                  const d = t.premium_direction || (CREDIT_STRATS.has(t.strategy) ? "credit" : DEBIT_STRATS.has(t.strategy) ? "debit" : null);
                  if (!d) return null;
                  return (
                    <span className={`text-[8px] ml-1 px-1 py-0.5 rounded-sm font-bold uppercase font-mono-data ${d === "credit" ? "text-emerald-400 bg-emerald-500/10" : "text-red-400 bg-red-500/10"}`} data-testid={`direction-chip-${t.id || t.ticker}`}>{d}</span>
                  );
                })()}
              </>
            )}
            {t.source === "ibkr_import" && (
              <span className="text-[8px] ml-1 px-1 py-0.5 rounded-sm font-bold uppercase font-mono-data text-sky-400 bg-sky-500/10" title="Imported from broker trade log" data-testid={`import-chip-${t.id || t.ticker}`}>IMPORTED</span>
            )}
          </td>
        );
      case "strike":
        return <td key={key} className="text-right font-mono-data text-xs" style={{ color: "var(--hoip-text-secondary)" }}>{strikeDisplay(t)}</td>;
      case "entry":
        return <td key={key} className="text-right font-mono-data text-xs">{t.entry_price != null ? `$${t.entry_price.toFixed(2)}` : "—"}</td>;
      case "exit":
        return <td key={key} className="text-right font-mono-data text-xs">{t.exit_price != null ? `$${t.exit_price.toFixed(2)}` : "—"}</td>;
      case "pnl":
        return <td key={key} className={`text-right font-mono-data text-xs ${t.pnl > 0 ? "text-emerald-500" : t.pnl < 0 ? "text-red-500" : ""}`}>{t.pnl != null ? `$${t.pnl.toFixed(2)}` : "—"}</td>;
      case "currpct": {
        const pct = currPnlPct(t);
        return <td key={key} className="text-right font-mono-data text-xs">{pct !== null ? <span className={pct >= 0 ? "text-emerald-500" : "text-red-500"}>{pct >= 0 ? "+" : ""}{pct.toFixed(1)}%</span> : "—"}</td>;
      }
      case "status":
        return <td key={key}><span className={`text-xs px-2 py-0.5 border font-mono-data ${t.status === "open" ? "text-blue-400 border-blue-800" : t.status === "closed" ? "text-emerald-500 border-emerald-800" : "text-yellow-500 border-yellow-800"}`}>{t.status}</span></td>;
      case "date":
        return <td key={key} className="text-xs font-mono-data" style={{ color: "var(--hoip-text-secondary)" }}>{t.entry_date || t.exit_date || "—"}</td>;
      default:
        return <td key={key} />;
    }
  };

  return (
    <div className="border" style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }}>
      <div className="flex items-center gap-2 p-2 border-b" style={{ borderColor: "var(--hoip-border)" }}>
        <div className="relative flex-1 max-w-xs">
          <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2" style={{ color: "var(--hoip-text-tertiary)" }} />
          <Input value={search} onChange={(e) => onSearchChange(e.target.value)} placeholder="Search ticker…"
            className="bg-[#0A0A0A] border-[#27272A] rounded-sm h-7 text-xs font-mono-data pl-7" data-testid="trade-search-input" />
        </div>
        <div className="relative ml-auto">
          <button onClick={() => setShowCols(v => !v)}
            className="flex items-center gap-1 h-7 px-2 text-[10px] border rounded-sm hover:bg-[#1A1A1A]"
            style={{ borderColor: "var(--hoip-border)", color: "var(--hoip-text-secondary)" }} data-testid="column-settings-btn">
            <Columns3 className="w-3 h-3" /> Columns
          </button>
          {showCols && (
            <div className="absolute right-0 top-8 z-30 border rounded-sm p-2 w-48 space-y-1"
              style={{ background: "#111111", borderColor: "var(--hoip-border)" }} data-testid="column-settings-menu">
              <div className="text-[9px] uppercase font-bold pb-1" style={{ color: "var(--hoip-text-tertiary)" }}>Show / hide · drag headers to reorder</div>
              {prefs.order.map(k => {
                const col = GRID_COLUMNS.find(c => c.key === k);
                if (!col) return null;
                return (
                  <label key={k} className="flex items-center gap-2 text-[11px] cursor-pointer hover:bg-[#1A1A1A] px-1 py-0.5 rounded-sm"
                    style={{ color: "var(--hoip-text-secondary)" }}>
                    <input type="checkbox" checked={!prefs.hidden.includes(k)} onChange={() => toggleCol(k)} data-testid={`col-toggle-${k}`} />
                    {col.label}
                  </label>
                );
              })}
              <button onClick={() => onPrefsChange({ ...DEFAULT_GRID_PREFS })}
                className="w-full mt-1 h-6 text-[10px] border rounded-sm hover:bg-[#1A1A1A]"
                style={{ borderColor: "var(--hoip-border)", color: "var(--hoip-text-tertiary)" }} data-testid="reset-layout-btn">
                Reset layout
              </button>
            </div>
          )}
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full dense-table" data-testid={`trade-log-${tab}-table`}>
          <thead><tr className="border-b" style={{ borderColor: "var(--hoip-border)" }}>
            {visibleCols.map(col => (
              <th key={col.key} draggable
                onDragStart={() => setDragKey(col.key)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => { reorder(dragKey, col.key); setDragKey(null); }}
                onClick={() => setSort(col.sortKey)}
                className={`text-${col.align} text-xs font-medium cursor-pointer select-none hover:text-white`}
                style={{ color: "var(--hoip-text-tertiary)" }}
                title="Click to sort · drag to reorder"
                data-testid={`sort-header-${col.key}`}>
                <span className={`inline-flex items-center gap-0.5 ${col.align === "right" ? "justify-end" : ""}`}>
                  <GripVertical className="w-2.5 h-2.5 opacity-30" />
                  {col.label}
                  {prefs.sort.key === col.sortKey && (prefs.sort.dir === "asc" ? <ArrowUp className="w-2.5 h-2.5" /> : <ArrowDown className="w-2.5 h-2.5" />)}
                </span>
              </th>
            ))}
            <th className="text-right text-xs font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Actions</th>
          </tr></thead>
          <tbody>
            {rows.map((t, i) => (
              <tr key={t.id || i} className="border-b hover:bg-[#1A1A1A]" style={{ borderColor: "var(--hoip-divider)" }}>
                {visibleCols.map(col => renderCell(t, col.key))}
                <td className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    {t.status === "open" && (<button onClick={() => onCloseTrade(t)} className="p-1 hover:bg-emerald-900/30 rounded-sm" title="Close trade" data-testid={`close-trade-${t.ticker}`}><CheckCircle className="w-3 h-3 text-emerald-500" /></button>)}
                    <button onClick={() => onEdit(t)} className="p-1 hover:bg-[#1A1A1A] rounded-sm" title="Edit" data-testid={`edit-trade-${t.ticker}`}><BookOpen className="w-3 h-3" style={{ color: "var(--hoip-text-tertiary)" }} /></button>
                    <button onClick={() => onDelete(t.id)} className="p-1 hover:bg-red-900/30 rounded-sm" title="Delete" data-testid={`delete-trade-${t.ticker}`}><Trash2 className="w-3 h-3 text-red-500" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (<tr><td colSpan={visibleCols.length + 1} className="text-center py-8 text-xs" style={{ color: "var(--hoip-text-tertiary)" }}>{q ? `No trades matching "${q}".` : `No ${tab === "all" ? "" : tab} trades logged yet.`}</td></tr>)}
          </tbody>
        </table>
      </div>
    </div>
  );
};
